import axios from 'axios';
import { getApiTimeout as getConfigTimeout, getLongApiTimeout, appConfig } from './appConfig';

// global system error handler for API errors
let systemErrorHandler: ((unavailable: boolean, error?: string) => void) | null = null;

export const setSystemErrorHandler = (handler: (unavailable: boolean, error?: string) => void) => {
  systemErrorHandler = handler;
};
/**
 * Common API configuration for all API clients
 */

// Get timeout values from centralized configuration
// NOTE: evaluated as a getter function, NOT a module-level constant.
// Previously this was:  export const API_TIMEOUTS = { DEFAULT: getConfigTimeout(), ... }
// That evaluated ONCE at module import time — before appConfig had resolved the
// correct environment from window.location.hostname.  Any module that imported
// apiConfig.ts early (e.g. during webpack chunk evaluation) would get stale
// timeout values (defaulting to LOCAL = 15 000 ms) regardless of the actual env.
// Now we expose a getter so every call reads the live appConfig value.
export const getApiTimeouts = () => ({
  DEFAULT: getConfigTimeout(),
  LONG: getLongApiTimeout(),
});

// Keep API_TIMEOUTS as a computed property for backward-compat with existing imports
export const API_TIMEOUTS = {
  get DEFAULT() { return getConfigTimeout(); },
  get LONG()    { return getLongApiTimeout(); },
};

// API timeout types for better type safety
export type ApiTimeoutType = 'default' | 'long';

// Get the appropriate timeout for different API types
export const getApiTimeout = (type: ApiTimeoutType = 'default'): number => {
  return type === 'long' ? API_TIMEOUTS.LONG : API_TIMEOUTS.DEFAULT;
};

// API Base URLs
export const API_ENDPOINTS = {
  MAIN: appConfig.API_BASE_URL
};

// API configuration loaded - timeout logging removed to reduce console noise

// Helper function to create API client with specific timeout
export const createApiClientWithTimeout = (
  timeoutType: ApiTimeoutType,
  baseURL?: string,
  additionalConfig?: any
) => {
  return axios.create({
    baseURL: baseURL || API_ENDPOINTS.MAIN,
    timeout: getApiTimeout(timeoutType),
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
    ...additionalConfig
  });
};

// Error handling utility for API failures
export const handleApiError = (error: any, operation: string) => {
  const status = error.response?.status;
  const endpoint = error.config?.url;
  const method = error.config?.method?.toUpperCase();
  const timestamp = new Date().toISOString();

  // Timeout errors
  if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
    console.error(`[${operation}] Request timeout:`, {
      endpoint,
      method,
      timeout: error.config?.timeout,
      time: timestamp,
    });
    return { isRetryable: true, reason: 'timeout' };
  }

  // Network connectivity issues
  if (error.code === 'ECONNREFUSED' || 
      error.code === 'ERR_NETWORK' || 
      error.code === 'ENOTFOUND' ||
      error.message === 'Network Error' ||
      !error.response) {
    
    console.error(`[${operation}] Network error:`, {
      endpoint,
      method,
      code: error.code,
      time: timestamp,
    });
    
    // Trigger system unavailable page
    if (systemErrorHandler) {
      systemErrorHandler(true, 'Unable to connect to the server. Please check your network connection.');
    }
    
    return { isRetryable: true, reason: 'network_error', redirect: true };
  }

  // 500+ Server errors
  if (status >= 500) {
    console.error(`[${operation}] Server error ${status}:`, {
      endpoint,
      method,
      status,
      statusText: error.response?.statusText,
      time: timestamp,
    });
    
    // Trigger system unavailable page
    if (systemErrorHandler) {
      systemErrorHandler(true, `Server error (${status}): ${error.response?.statusText || 'Internal server error'}`);
    }
    
    return { isRetryable: true, reason: 'server_error', redirect: true };
  }

  // 401 Unauthorized - handled by interceptor, minimal logging
  if (status === 401) {
    console.warn(`[${operation}] Unauthorized (401):`, { endpoint, method, time: timestamp });
    return { isRetryable: true, reason: 'auth_required' };
  }

  // 403 Forbidden
  if (status === 403) {
    console.warn(`[${operation}] Forbidden (403):`, { endpoint, method, time: timestamp });
    return { isRetryable: false, reason: 'forbidden' };
  }

  // 404 Not Found
  if (status === 404) {
    console.warn(`[${operation}] Not found (404):`, { endpoint, method, time: timestamp });
    return { isRetryable: false, reason: 'not_found' };
  }

  // 429 Too Many Requests
  if (status === 429) {
    console.warn(`[${operation}] Rate limited (429):`, { endpoint, method, time: timestamp });
    return { isRetryable: true, reason: 'rate_limited' };
  }

  // Other 4xx client errors
  if (status >= 400 && status < 500) {
    console.warn(`[${operation}] Client error ${status}:`, {
      endpoint,
      method,
      status,
      message: error.response?.data?.message || error.message,
      time: timestamp,
    });
    return { isRetryable: false, reason: 'client_error' };
  }

  // Unknown errors
  console.error(`[${operation}] Unknown error:`, {
    endpoint,
    method,
    message: error.message,
    code: error.code,
    time: timestamp,
  });
  return { isRetryable: false, reason: 'unknown' };
};

const apiConfig = {
  API_TIMEOUTS,
  getApiTimeout,
  API_ENDPOINTS,
  createApiClientWithTimeout,
  handleApiError
};

export default apiConfig;
